import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNetworkStatus } from '../../context/NetworkStatusContext';
import notificationService from '../../services/notificationService';
import teamService from '../../services/teamService';
import { STORAGE_KEYS } from '../../services/api';
import {
  LayoutDashboard,
  Building2,
  Network,
  ClipboardList,
  FolderOpen,
  BarChart3,
  Video,
  HelpCircle,
  MessageCircle,
  Settings,
  LogOut,
  Menu,
  X,
  Globe,
  Bell,
  ChevronDown,
  Users,
  Receipt,
  FileCheck,
  Sun,
  Moon,
  ArrowLeftRight,
  RotateCcw,
  MoreHorizontal,
  Crown
} from 'lucide-react';
import './Navbar.css';

// localStorage key for storing the previous session when switching accounts
const PREVIOUS_SESSION_KEY = 'msk_previous_session';
// Keep the old hamburger account block disabled because the mobile header now has its own account button.
const SHOW_LEGACY_MOBILE_ACCOUNT_SECTION = false;

const getSafeAccountImageSrc = (image) => {
  if (!image || typeof image !== 'string') return null;
  const value = image.trim();
  return /^(https?:|data:image\/|blob:|\/)/i.test(value) ? value : null;
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, refreshUser } = useAuth();

  // Sub-accounts show "شركة الحساب الرئيسي / اسمك"
  const displayName = (user?.accountType === 'sub' && user?.parentUser?.name)
    ? `${user.parentUser.name} / ${user.name}`
    : user?.name;
  const { toggleTheme, isDark } = useTheme();
  const { networkEnabled } = useNetworkStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [switchingTo, setSwitchingTo] = useState(null); // memberId being switched to
  const [showMoreMobile, setShowMoreMobile] = useState(false); // mobile "المزيد" expand toggle
  const [showAccountMenu, setShowAccountMenu] = useState(false); // desktop account dropdown toggle
  const [accountImageFailed, setAccountImageFailed] = useState(false);
  const moreMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const accountImageSrc = !accountImageFailed
    ? getSafeAccountImageSrc(user?.avatarUrl || user?.avatar)
    : null;
  const accountInitial = user?.name?.charAt(0) || displayName?.charAt(0) || 'م';

  // Check if this session was started via account switch (so we can show "return" button)
  const previousSession = (() => {
    try {
      const raw = localStorage.getItem(PREVIOUS_SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // Fetch unread notifications count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (location.pathname !== '/notifications') {
      fetchUnreadCount();
    }
  }, [location.pathname, fetchUnreadCount]);

  useEffect(() => {
    // Retry a fresh account/logo image whenever the signed URL or avatar value changes.
    setAccountImageFailed(false);
  }, [user?.avatarUrl, user?.avatar]);

  useEffect(() => {
    if (user?.avatar && !user?.avatarUrl && typeof refreshUser === 'function') {
      // Some login payloads only include the R2 key; refresh profile data to get avatarUrl.
      refreshUser();
    }
  }, [user?.avatar, user?.avatarUrl, refreshUser]);

  // Fetch team members for main accounts so we can show the switch list
  useEffect(() => {
    if (user?.accountType === 'main') {
      teamService.getTeam()
        .then(res => {
          if (res?.success && Array.isArray(res.data)) {
            setTeamMembers(res.data);
          }
        })
        .catch(() => {});
    }
  }, [user?.accountType]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    // Also clear any saved previous session on logout
    localStorage.removeItem(PREVIOUS_SESSION_KEY);
    setIsMobileMenuOpen(false);
    setShowMoreMobile(false);
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    // Keep hidden mobile account actions safe if this block is re-enabled later.
    setIsMobileMenuOpen(false);
    setShowMoreMobile(false);
  };

  const handleMobileThemeToggle = () => {
    toggleTheme();
    closeMobileMenu();
  };

  const handleMobileLanguageToggle = () => {
    toggleLanguage();
    closeMobileMenu();
  };

  // Switch into a team member's account
  const handleSwitchToMember = async (memberId) => {
    setSwitchingTo(memberId);
    try {
      const res = await teamService.switchToMember(memberId);
      if (!res?.success) {
        alert(res?.message || 'فشل تبديل الحساب');
        return;
      }

      // Save the current session so we can restore it later
      const currentSession = {
        accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
        refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
        userData: localStorage.getItem(STORAGE_KEYS.USER_DATA),
      };
      localStorage.setItem(PREVIOUS_SESSION_KEY, JSON.stringify(currentSession));

      // Store the new member's tokens and data
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, res.data.accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, res.data.refreshToken);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(res.data.user));

      // Reload so all contexts re-initialize with the new session
      window.location.href = '/dashboard';
    } catch (err) {
      alert('حدث خطأ أثناء تبديل الحساب');
    } finally {
      setSwitchingTo(null);
    }
  };

  // Return to the main account session (or back to admin app)
  const handleReturnToMain = () => {
    if (!previousSession) return;

    if (previousSession.returnUrl) {
      // Came from admin switch — restore admin tokens then go to admin app
      localStorage.setItem('msk_admin_access_token', previousSession.accessToken);
      localStorage.setItem('msk_admin_refresh_token', previousSession.refreshToken);
      localStorage.setItem('msk_admin_user_data', previousSession.user);
      // Clear the user tokens
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(PREVIOUS_SESSION_KEY);
      window.location.href = previousSession.returnUrl;
    } else {
      // Came from team-member switch — restore main user tokens
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, previousSession.accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, previousSession.refreshToken);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, previousSession.userData);
      localStorage.removeItem(PREVIOUS_SESSION_KEY);
      window.location.href = '/dashboard';
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainNavItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/offers', label: t('nav.myOffers'), icon: Building2 },
    // أبقِ السوق ظاهرًا للتعريف به، لكن امنع فتحه إلى أن يصبح جاهزًا.
    { path: '/network', label: t('nav.network'), icon: Network, disabled: true },
    { path: '/requests', label: t('nav.requests'), icon: ClipboardList },
    { path: '/catalog', label: t('nav.catalog'), icon: FolderOpen },
  ];

  const moreNavItems = [
    // "المتابعين" مرتبطة بميزة السوق — تُخفى معها
    ...(networkEnabled ? [{ path: '/following', label: t('nav.following'), icon: Users }] : []),
    { path: '/statistics', label: t('nav.statistics'), icon: BarChart3 },
    { path: '/invoices', label: 'الفواتير', icon: Receipt },
    { path: '/notary-offices', label: t('nav.notaryOffices', 'مكاتب التوثيق'), icon: FileCheck },
    { path: '/tutorials', label: t('nav.tutorials'), icon: Video },
    { path: '/faq', label: t('nav.faq'), icon: HelpCircle },
    { path: '/chat', label: t('nav.chat'), icon: MessageCircle },
    { path: '/subscription', label: 'الباقة والترقية', icon: Crown },
    { path: '/settings', label: t('nav.settings'), icon: Settings }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Banner: shown when browsing as a switched sub-account or after admin switch */}
      {previousSession && (
        <div className="switch-account-banner">
          <span>
            {previousSession.returnUrl
              ? <>أنت تتصفح كمستخدم من لوحة التحكم: <strong>{user?.name}</strong></>
              : <>أنت تتصفح كحساب فرعي: <strong>{user?.name}</strong></>
            }
          </span>
          <button className="return-main-btn" onClick={handleReturnToMain}>
            <RotateCcw size={14} />
            {previousSession.returnUrl ? 'العودة للوحة التحكم' : 'العودة لحسابي'}
          </button>
        </div>
      )}

      <nav className="navbar">
        <div className="navbar-container">
          {/* Brand always navigates home; account actions live in the mobile menu. */}
          <div className="navbar-brand-wrapper">
            <Link to="/dashboard" className="navbar-brand">
              <div className="brand-icon">
                <img src="/icon-192.png" alt={t('common.appName')} className="brand-icon-image" />
              </div>
              <div className="brand-text">
                <span className="brand-name">{t('common.appName')}</span>
                <span className="brand-tagline">للتسويق العقاري</span>
              </div>
            </Link>
          </div>

          {/* Main Navigation - Desktop */}
          <div className="navbar-nav">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return item.disabled ? (
                <button key={item.path} type="button" className="nav-link nav-link-disabled" disabled aria-disabled="true">
                  <Icon size={18} /><span>{item.label}</span><small className="coming-soon-badge">قريبًا</small>
                </button>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* More dropdown: navigation links only. */}
            <div className="nav-dropdown" ref={moreMenuRef}>
              <button
                className={`nav-link dropdown-toggle ${moreNavItems.some(item => isActive(item.path)) ? 'active' : ''}`}
                onClick={() => setShowMoreMenu(!showMoreMenu)}
              >
                <span>{t('nav.more')}</span>
                <ChevronDown size={16} />
              </button>
              {showMoreMenu && (
                <div className="dropdown-menu more-menu">
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <Icon size={18} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Section */}
          <div className="navbar-actions">
            {/* Notifications */}
            <Link to="/notifications" className="action-btn notification-btn" title={t('nav.notifications')}>
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </Link>

            {/* Desktop account menu keeps account actions separate from navigation. */}
            <div className="account-dropdown" ref={accountMenuRef}>
              <button
                className={`account-menu-btn ${showAccountMenu ? 'open' : ''}`}
                onClick={() => {
                  // On mobile the account button replaces account actions inside the hamburger menu.
                  setIsMobileMenuOpen(false);
                  setShowMoreMobile(false);
                  setShowAccountMenu(prev => !prev);
                }}
                title="الحساب"
              >
                <span className="account-menu-avatar">
                  {accountImageSrc ? (
                    <img
                      src={accountImageSrc}
                      alt={displayName || 'الحساب'}
                      onError={() => setAccountImageFailed(true)}
                    />
                  ) : (
                    <span>{accountInitial}</span>
                  )}
                </span>
                <ChevronDown size={15} />
              </button>

              {showAccountMenu && (
                <div className="dropdown-menu account-menu">
                  <div className="user-info-header account-info-header">
                    <div className="user-avatar large">
                      {accountImageSrc ? (
                        <img
                          src={accountImageSrc}
                          alt={displayName || 'الحساب'}
                          onError={() => setAccountImageFailed(true)}
                        />
                      ) : (
                        <span>{accountInitial}</span>
                      )}
                    </div>
                    <div className="user-details">
                      <span className="name">{displayName}</span>
                      {user?.email && <span className="email">{user.email}</span>}
                      <span className="role">مسوّق عقاري</span>
                    </div>
                  </div>

                  <Link
                    to="/settings"
                    className="dropdown-item"
                    onClick={() => setShowAccountMenu(false)}
                  >
                    <Settings size={18} />
                    <span>الإعدادات</span>
                  </Link>
                  <Link
                    to="/settings?tab=landing"
                    className="dropdown-item"
                    onClick={() => setShowAccountMenu(false)}
                  >
                    <Globe size={18} />
                    <span>موقعي العقاري</span>
                  </Link>
                  <button className="dropdown-item" onClick={() => { toggleTheme(); setShowAccountMenu(false); }}>
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                    <span>{isDark ? 'الوضع الفاتح' : 'الوضع الداكن'}</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { toggleLanguage(); setShowAccountMenu(false); }}>
                    <Globe size={18} />
                    <span>{i18n.language === 'ar' ? 'English' : 'العربية'}</span>
                  </button>

                  {user?.accountType === 'main' && teamMembers.length > 0 && (
                    <>
                      <div className="menu-divider"></div>
                      <div className="switch-section-label">تبديل الحساب</div>
                      {teamMembers.map((member) => (
                        <button
                          key={member._id}
                          className="dropdown-item switch-member-btn"
                          onClick={() => { setShowAccountMenu(false); handleSwitchToMember(member._id); }}
                          disabled={switchingTo === member._id}
                        >
                          <div className="switch-member-avatar">
                            {member.avatar
                              ? <img src={member.avatar} alt={member.name} />
                              : <span>{member.name?.charAt(0)}</span>
                            }
                          </div>
                          <span>{member.name}</span>
                          {switchingTo === member._id
                            ? <span className="switch-loading">...</span>
                            : <ArrowLeftRight size={14} className="switch-icon" />
                          }
                        </button>
                      ))}
                    </>
                  )}

                  <div className="menu-divider"></div>
                  <button className="dropdown-item logout" onClick={() => { setShowAccountMenu(false); handleLogout(); }}>
                    <LogOut size={18} />
                    <span>{t('common.logout')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={`mobile-menu-btn${isMobileMenuOpen ? ' open' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content">
          {/* رابط الرئيسية واضح في بداية قائمة الجوال للرجوع السريع للداشبورد. */}
          <Link
            to="/dashboard"
            className={`mobile-nav-link ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <LayoutDashboard size={20} />
            <span>الرئيسية</span>
          </Link>

          {/* Main nav items always visible after home */}
          {mainNavItems.filter((item) => item.path !== '/dashboard').map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}

          {/* "المزيد" toggle button */}
          <button
            className={`mobile-nav-link mobile-more-toggle ${showMoreMobile ? 'expanded' : ''}`}
            onClick={() => setShowMoreMobile(prev => !prev)}
          >
            <MoreHorizontal size={20} />
            <span>المزيد</span>
            <ChevronDown size={16} className={`mobile-more-chevron ${showMoreMobile ? 'open' : ''}`} />
          </button>

          {/* Collapsible section — animates in/out via CSS */}
          <div className={`mobile-more-section ${showMoreMobile ? 'open' : ''}`}>
            {moreNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link mobile-more-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => { setIsMobileMenuOpen(false); setShowMoreMobile(false); }}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {SHOW_LEGACY_MOBILE_ACCOUNT_SECTION && (
            <>
          {/* Mobile account actions are grouped here instead of behind the brand icon. */}
          <div className="mobile-account-section">
            <div className="mobile-account-header">
              <div className="mobile-account-avatar">
                {(user?.avatarUrl || user?.avatar) ? (
                  <img src={user.avatarUrl || user.avatar} alt={user?.name} />
                ) : (
                  <span>{user?.name?.charAt(0)}</span>
                )}
              </div>
              <div className="mobile-account-info">
                <span className="mobile-account-name">{displayName}</span>
                <span className="mobile-account-role">مسوّق عقاري</span>
              </div>
            </div>

            <button
              className="mobile-account-action"
              onClick={() => { closeMobileMenu(); navigate('/settings'); }}
            >
              <Settings size={18} />
              <span>الإعدادات</span>
            </button>
            <button
              className="mobile-account-action"
              onClick={() => { closeMobileMenu(); navigate('/settings?tab=landing'); }}
            >
              <Globe size={18} />
              <span>موقعي العقاري</span>
            </button>
            <button className="mobile-account-action" onClick={handleMobileThemeToggle}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
              <span>{isDark ? 'الوضع الفاتح' : 'الوضع الداكن'}</span>
            </button>
            <button className="mobile-account-action" onClick={handleMobileLanguageToggle}>
              <Globe size={18} />
              <span>{i18n.language === 'ar' ? 'English' : 'العربية'}</span>
            </button>

            {user?.accountType === 'main' && teamMembers.length > 0 && (
              <>
                <div className="mobile-account-label">تبديل الحساب</div>
                {teamMembers.map((member) => (
                  <button
                    key={member._id}
                    className="mobile-account-action"
                    onClick={() => { closeMobileMenu(); handleSwitchToMember(member._id); }}
                    disabled={switchingTo === member._id}
                  >
                    <div className="switch-member-avatar">
                      {member.avatar
                        ? <img src={member.avatar} alt={member.name} />
                        : <span>{member.name?.charAt(0)}</span>
                      }
                    </div>
                    <span>{member.name}</span>
                    {switchingTo === member._id
                      ? <span className="switch-loading">...</span>
                      : <ArrowLeftRight size={14} className="switch-icon" />
                    }
                  </button>
                ))}
              </>
            )}

            <button className="mobile-account-action mobile-account-logout" onClick={handleLogout}>
              <LogOut size={18} />
              <span>تسجيل الخروج</span>
            </button>
          </div>

            </>
          )}

          {/* Mobile: return to main account (shown only when browsing as a switched account) */}
          {previousSession && (
            <button className="mobile-nav-link return-main" onClick={handleReturnToMain}>
              <RotateCcw size={20} />
              <span>العودة لحسابي الرئيسي</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} />
      )}
    </>
  );
};

export default Navbar;
